import React from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useFileStore } from './FileProvider';
import { HtmlTooltip, StyledDarkRedInformationTypography } from "../../../../components/commonComponents/StyledComponents/styled";
import { Close } from "@mui/icons-material";
import { loadLoopCampaignVisuals } from "../../../../api_calls/loopCampaign";
import { useSelector } from "react-redux";
import { checkUserDetailsFromCookie } from "../../../../api_calls/userManagement";
import TemporaryThumbnail from "./thumbnail/TemporaryThumbnail";
import ActiveInactiveVisualsList from "./ActiveInactiveVisualsList";

const thumbsContainer = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 16
};

const Visuals = ({ usedFor, loopInfo }) => {
    const {
        acceptedFiles,
        duplicateFiles,
        rejectedFiles,
        longDurationRejectedFiles,
        maxFilesReached,
        resolutionRejectedFiles,
        invalidFiles,
        addFiles,
        removeFile
    } = useFileStore();

    const currentStateData = useSelector((state) => state.userSelections);
    const checkUserDetails = checkUserDetailsFromCookie();
    let userDetailObj = {};
    if (checkUserDetails.success === true) {
        userDetailObj = checkUserDetails.user_detail;
    }

    const [previews, setPreviews] = React.useState([]);
    const [fileDialogActive, setFileDialogActive] = React.useState(false);

    const maxFileSize = 50 * 1024 * 1024;
    const minFileInBytes = 6;

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'video/mp4': ['.mp4'],
        },
        maxSize: maxFileSize,
        minSize: minFileInBytes,
        noDragEventsBubbling: true,
        onFileDialogOpen: () => {
            setFileDialogActive(true);
        },
        onFileDialogCancel: () => {
            setFileDialogActive(false); // reset when dialog is closed
        },
        // Disable drag while file dialog is open
        noDrag: fileDialogActive,
        disabled: usedFor === 'update' && currentStateData.uploaded_loop_files_length >= currentStateData?.new_loop_data?.visual_upload_limit ? true : false,
        onDrop: (accepted, rejected) => {
            setFileDialogActive(false);
            addFiles(accepted, rejected);
        },
    });

    const convertToMb = (bytesValue) => {
        return (bytesValue / (1024 * 1024)).toFixed(2);
    };

    const thumbs = previews.map(({ file, url }) => {
        const isImage = file.type?.startsWith("image/");
        const isVideo = file.type?.startsWith("video/");

        return (
            <Box key={file.name} position="relative">
                <div style={{ position: "relative" }}>
                    {/* <Thumbnail url={url} type={isImage ? "image" : "video"} /> */}
                    <TemporaryThumbnail file={file} url={url} />
                    <HtmlTooltip title={"Remove Visual"}>
                        <IconButton
                            size="small"
                            onClick={() => removeFile(file.name)}
                            sx={{
                                position: "absolute",
                                top: -14,
                                right: -4,
                                background: "#E6E6E6",
                                color: "#AF3241",
                                '&:hover': {
                                    background: "#AF3241",
                                    color: "#E6E6E6"
                                },
                            }}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </HtmlTooltip>

                </div>
            </Box>

        );
    });

    React.useEffect(() => {
        const newPreviews = acceptedFiles.map(file => {
            return {
                file,
                url: URL.createObjectURL(file),
            };
        });

        setPreviews(newPreviews);

        // load visuals if usedFor is 'edit'
        if (usedFor === 'update') {
            loadLoopCampaignVisuals(loopInfo.id)
        }

        // Cleanup object URLs on unmount or files change
        return () => {
            newPreviews.forEach(p => URL.revokeObjectURL(p.url));
        };
    }, [acceptedFiles]);

    return (
        <>
            <Box height={10} />
            <Typography variant="body2" sx={{ fontSize: "15px" }}>
                Upload the visuals which you want to add to the loop-based campaign. Videos that are part of a loop are always played without sound. Please avoid a duplication of file names within the same campaign.
            </Typography>
            <Box height={10} />
            <Typography variant="body2" sx={{ fontSize: "15px" }}>
                <i>For an optimal playout, image files should not exceed 5 MB.</i>
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "15px" }}>
                <i>For video files, the maximum allowed duration is:  {currentStateData?.new_loop_data?.visual_upload_duration} seconds.</i>
            </Typography>
            <Box height={20} />
            <Typography variant="body2" sx={{ fontSize: "15px" }}>
                <strong> Maximum number of visuals: {currentStateData?.new_loop_data?.visual_upload_limit}</strong>
            </Typography>
            <Box height={20} />

            {/* File Drop Area */}
            <Stack direction="row" spacing={2}>
                <Box
                    {...getRootProps({ className: "dropzone" })}
                    height={60}
                    width="100%"
                    sx={{
                        border: "2px dashed #888",
                        padding: "20px",
                        textAlign: "center",
                        borderRadius: "12px",
                        cursor: "pointer",
                    }}
                >
                    <input {...getInputProps()} />
                    {
                        usedFor === 'update' && currentStateData.uploaded_loop_files_length >= currentStateData?.new_loop_data?.visual_upload_limit ? (
                            <Box mt={2.5} sx={{ textAlign: "center" }}>
                                <Typography variant="body3" sx={{ margin: "20px" }}>
                                    Maximum file upload limit reached. You cannot upload more files.
                                </Typography>
                            </Box>
                        ) : <Box mt={1}>

                            <Typography variant="body3" sx={{ margin: "20px" }}>
                                Drag & Drop to Upload File(s) or Click to Browse
                            </Typography>
                            <Box height={4} />
                            <Typography variant="body2">
                                <em>.png, .jpg, .jpeg, or .mp4 (max. 1920x1080px)</em>
                            </Typography>
                        </Box>
                    }

                </Box>
            </Stack>

            {/* Accepted Files */}
            {acceptedFiles.length > 0 && (
                <Box mt={2}>
                    <h4>Accepted Files ({acceptedFiles.length})</h4>
                    <ul>
                        {acceptedFiles.map((file, index) => (
                            <li key={`${file.name}-${index}`} style={{ marginTop: "5px" }}>
                                <Stack direction="row" spacing={2}>
                                    <Typography variant="body2">
                                        {file.name} - {convertToMb(file.size)} MB
                                    </Typography>
                                </Stack>
                            </li>
                        ))}
                    </ul>
                </Box>
            )}

            {/* Rejected Files */}
            {(rejectedFiles.length > 0 || duplicateFiles.length > 0 || longDurationRejectedFiles.length > 0 || maxFilesReached.length > 0 || invalidFiles.length !== 0 || resolutionRejectedFiles.length > 0) && (
                <Box mt={2}>
                    <h4>Rejected Files ({parseInt(rejectedFiles.length + duplicateFiles.length + longDurationRejectedFiles.length + maxFilesReached.length + resolutionRejectedFiles.length)})</h4>
                    <ul>
                        {rejectedFiles.map((file, index) => (
                            <li key={`${file.name}-rejected-${index}`}>
                                <Stack direction="row" spacing={2}>
                                    <Box width={350} sx={{ wordWrap: "break-word" }}>
                                        <Typography variant="body2">
                                            {file.name} - {convertToMb(file.size)} MB
                                        </Typography>
                                    </Box>
                                    <Box width={650}>
                                        <StyledDarkRedInformationTypography variant="body2">
                                            : {file.size > maxFileSize
                                                ? "File size exceeds the limit of 50 MB"
                                                : file.size < minFileInBytes
                                                    ? "File is empty."
                                                    : "Invalid file type. Allowed types are: .mp4, .jpg, .jpeg, or .png"}
                                        </StyledDarkRedInformationTypography>
                                    </Box>

                                </Stack>
                            </li>
                        ))}
                        {/* ------ duplicate files ------- */}
                        {duplicateFiles.map((file, index) => (
                            <li key={`${file.name}-duplicate-${index}`}>
                                <Stack direction="row" spacing={2}>
                                    <Box width={350} sx={{ wordWrap: "break-word" }}>
                                        <Typography variant="body2">
                                            {file.name} - {convertToMb(file.size)} MB
                                        </Typography>
                                    </Box>
                                    <Box width={650}>
                                        <StyledDarkRedInformationTypography variant="body2">
                                            : Rejected: Duplicate file. This file will not be uploaded.
                                        </StyledDarkRedInformationTypography>
                                    </Box>

                                </Stack>
                            </li>
                        ))}
                        {/* --- rejected because of duration mismatch ----- */}
                        {
                            longDurationRejectedFiles.map((file, index) => (
                                <li key={`${file.name}-duplicate-${index}`}>
                                    <Stack direction="row" spacing={2}>
                                        <Box width={350} sx={{ wordWrap: "break-word" }}>
                                            <Typography variant="body2">
                                                {file.name} - {convertToMb(file.size)} MB
                                            </Typography>
                                        </Box>
                                        <Box width={650}>
                                            <StyledDarkRedInformationTypography variant="body2">
                                                : Rejected: Video duration too long. This file will not be uploaded.
                                            </StyledDarkRedInformationTypography>
                                        </Box>

                                    </Stack>
                                </li>
                            ))}
                        {/* ------- rejected because of resolution ----- */}
                        {
                            resolutionRejectedFiles.map((file, index) => (
                                <li key={`${file.name}-resolution-${index}`}>
                                    <Stack direction="row" spacing={2}>
                                        <Box width={350} sx={{ wordWrap: "break-word" }}>
                                            <Typography variant="body2">
                                                {file.name} - {convertToMb(file.size)} MB
                                            </Typography>
                                        </Box>
                                        <Box width={650}>
                                            <StyledDarkRedInformationTypography variant="body2">
                                                : Resolution too high. Resolution must not exceed 1920x1080px.
                                            </StyledDarkRedInformationTypography>
                                        </Box>

                                    </Stack>
                                </li>
                            ))}
                        {/* ------- rejected because of max upload limit ----- */}
                        {
                            maxFilesReached.map((file, index) => (
                                <li key={`${file.name}-max-files-${index}`}>
                                    <Stack direction="row" spacing={2}>
                                        <Box width={350} sx={{ wordWrap: "break-word" }}>
                                            <Typography variant="body2">
                                                {file.name} - {convertToMb(file.size)} MB
                                            </Typography>
                                        </Box>
                                        <Box width={650}>
                                            <StyledDarkRedInformationTypography variant="body2">
                                                : Rejected: Maximum number of visuals exceeded. This file will not be uploaded.
                                            </StyledDarkRedInformationTypography>
                                        </Box>

                                    </Stack>
                                </li>
                            ))
                        }
                        {/* -------- invalid file names ------------ */}
                        {invalidFiles.map((file, index) => (
                            <li key={`${file.name}-${index}`} style={{ marginTop: "5px" }}>
                                <Stack direction={"row"} spacing={2}>
                                    <Box width={350} sx={{ wordWrap: "break-word" }}>
                                        <Typography variant="body2">
                                            {file.name}
                                        </Typography>
                                    </Box>
                                    <Box width={450}>
                                        <StyledDarkRedInformationTypography variant="body2">
                                            :{" "}{"Invalid file name. The file name should only contain a-z, A-Z, 0-9, -, or _"}
                                        </StyledDarkRedInformationTypography>
                                    </Box>

                                </Stack>
                            </li>
                        ))}
                    </ul>
                </Box>
            )}

            <Box height={20} />
            {/* Thumbnails of accepted files */}
            <aside style={thumbsContainer}>
                <Box display="flex" flexWrap="wrap" gap={2}>
                    {thumbs}
                </Box>

            </aside>

            <Box height={20} />
            {/* -------- active/inactive visuals list if usedFor===update */}
            <ActiveInactiveVisualsList loopInfo={loopInfo} usedFor={"listReview"} />
        </>
    );
};

export default Visuals;
